import { useState } from "preact/hooks";
import type { InvoiceItem } from "../../lib/gboModels";
import { getApiFailureMessage } from "../../lib/gboApiClient";

type DownloadStatus = "idle" | "downloading" | "error";

export type DownloadEntry = {
  status: DownloadStatus;
  message: string | null;
};

const idleDownload: DownloadEntry = { status: "idle", message: null };

export function useInvoiceDownloads(downloads: {
  invoice: (id: string) => Promise<unknown>;
  receipt: (id: string) => Promise<unknown>;
}) {
  const [invoiceDownload, setInvoiceDownload] = useState<DownloadEntry>(idleDownload);
  const [receiptDownloads, setReceiptDownloads] = useState<Record<string, DownloadEntry>>({});

  async function downloadInvoice(id: string) {
    setInvoiceDownload({ status: "downloading", message: null });

    try {
      await downloads.invoice(id);
      setInvoiceDownload({ status: "idle", message: null });
    } catch (error) {
      setInvoiceDownload({
        status: "error",
        message: getApiFailureMessage(error, "Unable to download invoice."),
      });
    }
  }

  async function downloadReceipt(payment: InvoiceItem) {
    setReceiptDownload(payment.id, { status: "downloading", message: null });

    try {
      await downloads.receipt(payment.transactionId);
      setReceiptDownload(payment.id, { status: "idle", message: null });
    } catch (error) {
      setReceiptDownload(payment.id, {
        status: "error",
        message: getApiFailureMessage(error, "Unable to download receipt."),
      });
    }
  }

  function setReceiptDownload(id: string, download: DownloadEntry) {
    setReceiptDownloads((downloads) => ({
      ...downloads,
      [id]: download,
    }));
  }

  return { invoiceDownload, receiptDownloads, downloadInvoice, downloadReceipt };
}

export { idleDownload };
